import clamp from 'lodash/clamp'

import { PAGE_PARAM, PAGE_SIZE_PARAM } from '../../config/constants'
import { isEqualNullables } from '../common'

import {
  PAGINATION_DEFAULT_PAGE_SIZE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
  PAGINATION_PAGE_SIZES,
} from './pagination.constants'
import type { PaginationValue, PaginationValueParams } from './pagination.types'

/**
 * Returns last page.
 */
function getLastPage(count: number, pageSize: number): number {
  return Math.max(Math.ceil(count / pageSize) - 1, 0)
}

/**
 * Returns pagination page extracted from location params.
 */
export function getPageFromLocationParams(
  params: PaginationValueParams
): number {
  const page = parseInt(params[PAGINATION_PAGE_PARAM], 10)

  return page > 0 ? page - 1 : 0
}

/**
 * Returns pagination page size extracted from location params.
 */
export function getPageSizeFromLocationParams(
  params: PaginationValueParams
): number {
  const pageSize = parseInt(params[PAGINATION_PAGE_SIZE_PARAM], 10)

  return PAGINATION_PAGE_SIZES.includes(pageSize)
    ? pageSize
    : PAGINATION_DEFAULT_PAGE_SIZE
}

/**
 * Returns location params for pagination.
 */
export function getPaginationLocationParams(pagination: PaginationValue): {
  page: number | null
  page_size: number | null
} {
  const { page, pageSize } = pagination

  return {
    [PAGINATION_PAGE_PARAM]: page === 0 ? null : page + 1,
    [PAGINATION_PAGE_SIZE_PARAM]:
      pageSize === PAGINATION_DEFAULT_PAGE_SIZE ? null : pageSize,
  }
}

/**
 * Checks if the passed `page` is valid pagination page.
 */
export function isValidPaginationPage(
  page?: number | null,
  value?: Partial<PaginationValue> | null
): boolean {
  const { count = 0, pageSize = 0 } = value ?? {}
  const start = Number(page) * pageSize

  return (
    Number.isSafeInteger(pageSize) &&
    pageSize > 0 &&
    Number.isSafeInteger(start) &&
    start >= 0 &&
    start < count
  )
}

/**
 * Checks if the passed pagination value is valid.
 */
export function isValidPagination(value?: PaginationValue | null): boolean {
  return isValidPaginationPage(value?.page, value)
}

/**
 * Returns the last pagination page.
 */
export function getLastPaginationPage(pagination: PaginationValue): number {
  return getLastPage(pagination.count, pagination.pageSize)
}

/**
 * Return `true` if paginations are equal or all the pagination parameters are equal.
 */
export function isEqualPagination(
  pagination1: PaginationValue,
  pagination2: PaginationValue
): boolean {
  return (
    pagination1 === pagination2 ||
    (pagination1.count === pagination2.count &&
      pagination1.page === pagination2.page &&
      pagination1.pageSize === pagination2.pageSize)
  )
}

/**
 * Returns params for pagination to use inside API calls.
 */
export function getPaginationApiParams(
  pagination: Omit<PaginationValue, 'count'>
): {
  page: number
  page_size: number
} {
  return {
    [PAGE_PARAM]: pagination.page + 1,
    [PAGE_SIZE_PARAM]: pagination.pageSize,
  }
}

/**
 * Updates the `pagination` with new parameters and returns a new `PaginationValue` object
 * if the received parameters are not equal to the `pagination` parameters,
 * or `pagination` otherwise.
 */
export function updatePagination(
  pagination: Nullable<PaginationValue>,
  nextPaginationProps: Partial<PaginationValue>
): Nullable<PaginationValue> {
  const nextPagination = pagination && { ...pagination, ...nextPaginationProps }

  return isEqualNullables(pagination, nextPagination, isEqualPagination)
    ? pagination
    : nextPagination
}

/**
 * Updates the current page number and returns a new `PaginationValue` object
 * if the received page is not equal to the `pagination` parameter `page`,
 * or `pagination` otherwise.
 */
export function updatePaginationPage(
  pagination: Nullable<PaginationValue>,
  page: number
): Nullable<PaginationValue> {
  return pagination
    ? updatePagination(pagination, {
        page: clamp(page, 0, getLastPaginationPage(pagination)),
      })
    : pagination
}

/**
 * Updates the items count and returns a new `PaginationValue` object
 * if the received `count` is not equal to the `pagination` parameter `count`,
 * or `pagination` otherwise.
 */
export function updatePaginationCount(
  pagination: Nullable<PaginationValue>,
  count: number
): Nullable<PaginationValue> {
  return pagination
    ? updatePaginationPage(
        { ...pagination, count: clamp(count, 0, Number.MAX_SAFE_INTEGER) },
        pagination.page
      )
    : pagination
}
